import {
  IframeDialogClosed,
  OpenIframeDialog,
  UpdateIframeSrcDoc,
  UpdateIframeUrl,
} from "@components/utils/IframeDialog/IframeDialog";
import { EXPERIMENTS } from "@configs/statsig/constants";
import { useStatsigTracking } from "@configs/statsig/hooks";
import { getExperimentParameter } from "@configs/statsig/utils";
import { getCashbackPromptConfig } from "@constants/cashback-prompt";
import { WSCashbackRewardUpdatedEvent, WSCashbackRewardUpdatedEventDetail } from "@constants/ws";
import useGetRewardsInfo, { RewardInfoData } from "@hooks/useGetRewardsInfo";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useStatsigClient } from "@statsig/react-bindings";
import { RewardStatus } from "@types";
import { calculateTotalClaimableCashback, clearCooldownTimestamp, isEligibleForPrompt } from "@utils/cashback-prompt";
import logger from "@utils/logger";
import { getDiff } from "@utils/reward";
import { isGameIframeDialogOpen } from "@utils/ui";
import BigNumber from "bignumber.js";
import { WalletStatus, WalletTxMethod, bnOrZero } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWSCashbackRewardUpdated } from "./useAppWS";
import useAuthSession from "./useAuthSession";
import useProfileBalance from "./useProfileBalance";
import useServerInfo from "./useServerInfo";

const GAME_IFRAME_LOAD_DELAY_MS = 100;

const CASHBACK_TYPES = [
  WalletTxMethod.InstantCashBack,
  WalletTxMethod.DailyCashBack,
  WalletTxMethod.WeeklyCashBack,
  WalletTxMethod.MonthlyCashBack,
] as const;

/**
 * Convert WalletStatus from websocket payload to RewardInfoData format
 */
function buildRewardInfoFromWalletStatus(
  walletStatus: WalletStatus | null | undefined,
  walletType: WalletTxMethod,
  utcOffset: number,
): RewardInfoData | null {
  if (!walletStatus) return null;

  const remainingSeconds = getDiff(walletStatus, utcOffset);

  return {
    remainingSeconds: remainingSeconds,
    claimPoints: bnOrZero(walletStatus?.balance ?? 0)
      .dp(2, BigNumber.ROUND_DOWN)
      .toNumber(),
    periodInSeconds:
      moment(walletStatus?.nextPeriodResetDate).diff(walletStatus?.currentPeriodResetDate, "seconds") ?? 0,
    initialStatus: remainingSeconds > 0 ? RewardStatus.LOCKED : RewardStatus.CLAIMABLE,
    minClaimPoints: bnOrZero(walletStatus?.minimumClaim ?? 0)
      .dp(2, BigNumber.ROUND_DOWN)
      .toNumber(),
    type: walletType,
  };
}

/**
 * Convert websocket cashback payload to RewardInfoData format
 * Handles partial updates where fields may be omitted - only processes fields that exist in payload
 */
function convertWSPayloadToRewardData(
  payload: WSCashbackRewardUpdatedEventDetail,
  utcOffset: number,
): Partial<Record<WalletTxMethod, RewardInfoData | null>> {
  const result: Partial<Record<WalletTxMethod, RewardInfoData | null>> = {};

  // Convert cashback types from websocket payload
  // Only process fields that are actually present in the payload (partial updates)
  const cashbackMapping = {
    [WalletTxMethod.InstantCashBack]: payload.instant,
    [WalletTxMethod.DailyCashBack]: payload.daily,
    [WalletTxMethod.WeeklyCashBack]: payload.weekly,
    [WalletTxMethod.MonthlyCashBack]: payload.monthly,
  } as const;

  for (const type of CASHBACK_TYPES) {
    const walletStatus = cashbackMapping[type];
    // Only include in result if the field exists in payload (not undefined)
    if (walletStatus !== undefined) {
      result[type] = buildRewardInfoFromWalletStatus(walletStatus, type, utcOffset);
    }
  }

  return result;
}

/**
 * Extract cashback reward data from full rewards info
 */
function extractCashbackRewardData(
  rewardsInfo: Record<WalletTxMethod, RewardInfoData | null> | null,
): Record<WalletTxMethod, RewardInfoData | null> {
  const result = {} as Record<WalletTxMethod, RewardInfoData | null>;
  for (const type of CASHBACK_TYPES) {
    result[type] = rewardsInfo?.[type] ?? null;
  }
  return result;
}

/**
 * Clear a timeout stored in a ref
 */
function clearTimeoutRef(ref: { current: NodeJS.Timeout | null }): void {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}

/**
 * Hook to manage in-game cashback prompt visibility and state
 * Checks constraints and shows prompt when eligible during gameplay
 */
export function useInGameCashbackPrompt() {
  const { balance } = useProfileBalance({ skipAutoUpdate: false });
  const { latestDepositAmount } = useAuthSession();
  const serverInfo = useServerInfo();
  const { currency } = useMemberSettings();
  const config = getCashbackPromptConfig();
  const { client, isLoading } = useStatsigClient();
  const { data: initialRewardsInfo } = useGetRewardsInfo();
  const { trackCashbackMiniPromptShown } = useStatsigTracking();
  const [isVisible, setIsVisible] = useState(false);
  const wasVisibleRef = useRef(false);
  const [wsRewardData, setWsRewardData] = useState<Record<WalletTxMethod, RewardInfoData | null> | null>(null);
  const [isGameIframeOpened, setIsGameIframeOpened] = useState(false);
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const srcDocUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get experiment value - feature is enabled if experiment parameter is true
  const isFeatureEnabled = useMemo(() => {
    // Don't enable if client is still loading
    if (isLoading || !client) {
      logger._console.debug("[CashbackPrompt] Feature disabled: Statsig client not ready", {
        isLoading,
        hasClient: !!client,
      });
      return false;
    }

    // Get experiment to control exposures
    const experiment = client.getExperiment(EXPERIMENTS.unclaimedInstantCashbackMiniPrompt.name);
    const enabled = getExperimentParameter(
      experiment,
      EXPERIMENTS.unclaimedInstantCashbackMiniPrompt.parameters.unclaimedInstantCashbackMiniPrompt,
      false,
    );

    logger._console.debug("[CashbackPrompt] Feature enabled:", enabled, {
      experimentName: EXPERIMENTS.unclaimedInstantCashbackMiniPrompt.name,
    });

    return enabled;
  }, [client, isLoading]);

  // Use websocket reward data if available, otherwise fall back to initial reward data
  const effectiveRewardData = useMemo(() => {
    if (wsRewardData) {
      return wsRewardData;
    }
    return extractCashbackRewardData(initialRewardsInfo);
  }, [wsRewardData, initialRewardsInfo]);

  // Calculate total claimable cashback from effective reward data
  const totalClaimable = useMemo(() => {
    return calculateTotalClaimableCashback(effectiveRewardData);
  }, [effectiveRewardData]);

  // Check eligibility with reward data
  const checkEligibility = useCallback(
    (rewardDataToCheck: Record<WalletTxMethod, RewardInfoData | null> | null) => {
      logger._console.debug("[CashbackPrompt] Checking eligibility", {
        isGameIframeOpen: isGameIframeDialogOpen(),
        isFeatureEnabled,
        balance: balance?.toString(),
        latestDepositAmount,
        totalClaimable: calculateTotalClaimableCashback(rewardDataToCheck),
      });

      // Only check if game iframe is open
      if (!isGameIframeDialogOpen()) {
        logger._console.debug("[CashbackPrompt] Not eligible: Game iframe not open");
        setIsVisible(false);
        wasVisibleRef.current = false;
        return;
      }

      // Check if feature is enabled via Statsig experiment
      if (!isFeatureEnabled) {
        logger._console.debug("[CashbackPrompt] Not eligible: Feature disabled");
        setIsVisible(false);
        wasVisibleRef.current = false;
        return;
      }

      // Check all constraints
      const eligibility = isEligibleForPrompt(balance, latestDepositAmount, rewardDataToCheck, config.constraints);

      logger._console.debug("[CashbackPrompt] Eligibility check result", {
        eligible: eligibility.eligible,
        reasons: eligibility.reasons,
        constraints: config.constraints,
        totalClaimable: calculateTotalClaimableCashback(rewardDataToCheck),
      });

      const wasVisible = wasVisibleRef.current;
      setIsVisible(eligibility.eligible);
      wasVisibleRef.current = eligibility.eligible;

      if (eligibility.eligible && !wasVisible) {
        logger._console.log("[CashbackPrompt] ✅ Prompt will be shown");
        // Track prompt shown event whenever it transitions from not visible to visible
        const claimableAmount = calculateTotalClaimableCashback(rewardDataToCheck);
        trackCashbackMiniPromptShown({
          totalClaimable: claimableAmount,
          currency: currency,
          isInGame: isGameIframeDialogOpen(),
        });
      } else if (!eligibility.eligible) {
        logger._console.debug("[CashbackPrompt] ❌ Prompt will not be shown", {
          lowBalance: eligibility.reasons.lowBalance,
          cashbackThreshold: eligibility.reasons.cashbackThreshold,
          cooldown: eligibility.reasons.cooldown,
        });
      }
    },
    [balance, latestDepositAmount, config, isFeatureEnabled, trackCashbackMiniPromptShown, currency],
  );

  // Listen to websocket cashback updated events
  useWSCashbackRewardUpdated({
    cb: ({ detail }: WSCashbackRewardUpdatedEvent) => {
      logger._console.debug("[CashbackPrompt] Websocket cashback updated event received", {
        instant: detail.instant,
        daily: detail.daily,
        weekly: detail.weekly,
        monthly: detail.monthly,
      });

      // Convert websocket payload to reward data format (partial update)
      const utcOffset = serverInfo?.utcOffset ?? 0;
      const partialUpdate = convertWSPayloadToRewardData(detail, utcOffset);

      // Merge partial update with existing websocket data or initial server data
      setWsRewardData((prev) => {
        // If no previous WS data, merge with initial server data
        const baseData = prev ?? extractCashbackRewardData(initialRewardsInfo);

        // Merge partial update into base data
        const merged = {
          ...baseData,
          ...partialUpdate,
        };
        return merged;
      });
    },
  });

  // Check eligibility when wsRewardData changes
  useEffect(() => {
    if (wsRewardData) {
      checkEligibility(wsRewardData);
    }
  }, [wsRewardData, checkEligibility]);

  // Handle game iframe open/close events
  useEffect(() => {
    const handleIframeOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ options?: { isGame?: boolean } }>;
      const isGameIframe = customEvent.detail?.options?.isGame === true;

      if (isGameIframe) {
        logger._console.debug("[CashbackPrompt] Game iframe opened - resetting cooldown and hiding prompt");
        // Reset cooldown when new game session starts
        // Cooldown only applies within a game session, so reset for new session
        clearCooldownTimestamp();
        // Clear websocket data to force fresh fetch from server
        // This prevents showing stale data after claiming and reopening game
        setWsRewardData(null);
        // Hide any existing prompt when new game session starts
        // Don't show prompt immediately - only show when websocket events come in
        setIsVisible(false);
        wasVisibleRef.current = false;
        // Track that game iframe dialog has been opened (but game not yet loaded)
        setIsGameIframeOpened(true);
      }
    };

    const handleIframeClose = (e: Event) => {
      const customEvent = e as CustomEvent<{ options?: { isGame?: boolean } }>;
      const isGameIframe = customEvent.detail?.options?.isGame === true;

      if (isGameIframe) {
        logger._console.debug(
          "[CashbackPrompt] Game iframe closed - resetting cooldown, clearing WS data, and hiding prompt",
        );
        // Hide prompt when game iframe closes
        setIsVisible(false);
        wasVisibleRef.current = false;
        // Reset cooldown when game session ends
        // Cooldown only applies within a game session, so reset for next session
        clearCooldownTimestamp();
        // Clear websocket data to prevent stale data from carrying over to next session
        // This prevents race condition where websocket events arrive after close but before next open
        setWsRewardData(null);
        // Reset game iframe opened flag
        setIsGameIframeOpened(false);
      }
    };

    // Listen for iframe dialog events
    window.addEventListener(OpenIframeDialog, handleIframeOpen);
    window.addEventListener(IframeDialogClosed, handleIframeClose);

    return () => {
      window.removeEventListener(OpenIframeDialog, handleIframeOpen);
      window.removeEventListener(IframeDialogClosed, handleIframeClose);
    };
  }, []);

  // Handle iframe URL/SrcDoc updates - this indicates game has loaded after gateway fetch
  useEffect(() => {
    const handleIframeUpdate = (timeoutRef: { current: NodeJS.Timeout | null }, updateType: string) => {
      clearTimeoutRef(timeoutRef);

      if (!isGameIframeOpened) {
        return;
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;

        if (!isGameIframeDialogOpen()) {
          logger._console.debug("[CashbackPrompt] Game iframe not open after delay, skipping eligibility check");
          setIsGameIframeOpened(false);
          return;
        }

        logger._console.debug(
          `[CashbackPrompt] Game iframe ${updateType} updated - game loaded, checking eligibility with effective reward data`,
        );

        checkEligibility(effectiveRewardData);
        setIsGameIframeOpened(false);
      }, GAME_IFRAME_LOAD_DELAY_MS);
    };

    const handleIframeUrlUpdate = () => handleIframeUpdate(urlUpdateTimeoutRef, "URL");
    const handleIframeSrcDocUpdate = () => handleIframeUpdate(srcDocUpdateTimeoutRef, "SrcDoc");

    // Listen for iframe URL/SrcDoc update events
    window.addEventListener(UpdateIframeUrl, handleIframeUrlUpdate);
    window.addEventListener(UpdateIframeSrcDoc, handleIframeSrcDocUpdate);

    return () => {
      window.removeEventListener(UpdateIframeUrl, handleIframeUrlUpdate);
      window.removeEventListener(UpdateIframeSrcDoc, handleIframeSrcDocUpdate);
      clearTimeoutRef(urlUpdateTimeoutRef);
      clearTimeoutRef(srcDocUpdateTimeoutRef);
    };
  }, [isGameIframeOpened, effectiveRewardData, checkEligibility]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    logger._console.debug("[CashbackPrompt] Prompt dismissed by user");
    setIsVisible(false);
    wasVisibleRef.current = false;
  }, []);

  return {
    visible: isVisible,
    totalClaimable,
    onDismiss: handleDismiss,
  };
}
